// eslint-disable-next-line no-shadow
import find from 'lodash/find';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import size from 'lodash/size';
import uniq from 'lodash/uniq';
import filter from 'lodash/filter';
import replace from 'lodash/replace';
import sortBy from 'lodash/sortBy';

import {setWindowLocation} from 'js-utils/http';
import {
    logCheckpointOnPageChange,
    logCheckpointViaAjax,
} from 'js-utils/janus';

import {
    events as eventsDuck,
    location as locationDuck,
} from '@eb/destination-utils';

import {
    GA_BOOKMARK_ATTEMPT_ACTION,
    GA_BOOKMARK_EVENT_ACTION,
    GA_HOME_CATEGORY,
    GA_HOME_FEED_CATEGORY,
    GA_SEE_MORE_ACTION,
    GA_UNBOOKMARK_EVENT_ACTION,
    GA_VIEW_EVENT_ACTION,
    GA_SWITCH_TAB,
} from '../../constants/analytics';
import {
    LIKED_EVENTS,
    BOOKMARK_EVENT_FEED,
    SEE_MORE_FEED,
    VIEW_EVENT_FEED,
    SMALL_CITY_FALLBACK,
} from '../../constants/constants';
import {
    FEED_TABS,
    TABS_CONFIG,
} from '../../constants/tabConfig';
import {
    getUserSavedEvents,
    searchEvents,
    searchEventsWithParameters,
    fetchEventsForLocation as fetchEventsForLocationApi,
} from './api';
import {
    getTransformedContent,
    transformBucketsContent,
    transformSearchContent,
} from '../../utils/transformation';
import {getExperimentId} from '../../utils/feed';
import {
    updateContent,
    updateTabKey,
    verifyPoorContent,
} from '../content/actions';
import {setLoading} from '../app/actions';
// import {addOrganizers as fetchOrganizersForLocation} from '../organizers/actions';
// import {addOrganizerArticles as fetchOrganizerArticles} from '../organizerArticle/actions';

const {
    api: {
        saveEvent: saveEventApi,
        unsaveEvent: unsaveEventApi,
        loggedOutSaveRedirect,
    },
} = eventsDuck;

const {
    actions: {
        updateLocation,
    },
} = locationDuck;

const trackViewEvent = ({
    bucketKey,
    eventId,
    experimentId,
    tabKey,
    loggedIn = true,
}) => ({
    type: 'VIEW_EVENT',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_VIEW_EVENT_ACTION,
            label: replace(`${bucketKey}/${tabKey}`, ' ', ''),
            dimensions: {
                eventId,
                experimentId,
            },
        },
    },
});

const trackSaveEvent = ({eventId, experimentId, savedByYou}) => ({
    type: 'SAVE_EVENT',
    meta: {
        analytics: {
            category: GA_HOME_FEED_CATEGORY,
            action: savedByYou ? GA_BOOKMARK_EVENT_ACTION : GA_UNBOOKMARK_EVENT_ACTION,
            label: eventId,
            dimensions: {
                eventId,
                experimentId,
            },
        },
    },
});

export const trackTabChange = ({tabKey, loggedIn = true}) => ({
    type: 'CHANGE_TAB',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_SWITCH_TAB,
            label: tabKey,
        },
    },
});

export const clickOnViewEvent = (eventId, bucketKey) => (
    (dispatch, getState) => {
        const {
            app: {
                domain,
                experiments,
            },
            content: {
                tabIndex,
            },
            user: {
                isAuthenticated,
            },
        } = getState();
        const experimentId = getExperimentId(experiments);

        const configKey = FEED_TABS[tabIndex];

        const {tabKey} = TABS_CONFIG[configKey];

        logCheckpointOnPageChange(VIEW_EVENT_FEED, domain);

        dispatch(trackViewEvent({
            bucketKey,
            eventId,
            experimentId,
            tabKey,
            loggedIn: isAuthenticated,
        }));
    }
);

const trackSeeMore = ({bucketKey, experimentId, loggedIn = true}) => ({
    type: 'SEE_MORE_ACTION',
    meta: {
        analytics: {
            category: loggedIn ? GA_HOME_FEED_CATEGORY : GA_HOME_CATEGORY,
            action: GA_SEE_MORE_ACTION,
            label: bucketKey,
            dimensions: {
                experimentId,
            },
        },
    },
});

export const clickOnSeeMore = (url, bucketKey) => (
    (dispatch, getState) => {
        const state = getState();
        const {
            app: {experiments},
            user: {isAuthenticated},
        } = state;
        const experimentId = getExperimentId(experiments);

        logCheckpointViaAjax(SEE_MORE_FEED);

        dispatch(trackSeeMore({
            bucketKey,
            experimentId,
            loggedIn: isAuthenticated,
        }));

        setWindowLocation(url);
    }
);

export const UPDATE_EVENT_SAVED_BY_YOU = 'UPDATE_EVENT_SAVED_BY_YOU';
const updateEventSavedByYou = (eventId, savedByYou, tabKey = null) => ({
    type: UPDATE_EVENT_SAVED_BY_YOU,
    payload: {
        eventId,
        savedByYou,
        tabKey,
    },
});

export const UPDATE_LIKES_EVENT_BUCKET = 'UPDATE_LIKES_EVENT_BUCKET';
const updateLikesEventBucket = (events, eventIds) => ({
    type: UPDATE_LIKES_EVENT_BUCKET,
    payload: {
        events,
        eventIds,
    },
});

const _fetchUserSavedEvents = () => (
    (dispatch) => (
        getUserSavedEvents().then((response) => {
            const {
                entities: {
                    events: likedEvents,
                } = {},
                result: {
                    events: likedEventIds,
                },
            } = getTransformedContent('events', response);

            dispatch(updateLikesEventBucket(likedEvents, likedEventIds));
        })
    )
);

const _loggedInSaveAction = (dispatch, state, eventId, shouldSave) => {
    const {
        app: {
            experiments,
        },
        content: {
            entityContext,
            tabIndex,
        },
    } = state;
    const experimentId = getExperimentId(experiments);
    const userSavedEvents = find(entityContext, {key: LIKED_EVENTS});
    const configKey = FEED_TABS[tabIndex];
    const {tabKey} = TABS_CONFIG[configKey];

    if (shouldSave) {
        // optimistic update
        dispatch(updateEventSavedByYou(eventId, true));

        logCheckpointViaAjax(BOOKMARK_EVENT_FEED);

        return saveEventApi(eventId).then(() => {
            dispatch(trackSaveEvent({
                eventId,
                experimentId,
                savedByYou: true,
            }));

            // Resolved promise so view knows if the event save was successful
            return Promise.resolve('success');
        }).catch(() => {
            dispatch(updateEventSavedByYou(eventId, false));
        });
    }

    // optimistic update
    dispatch(updateEventSavedByYou(eventId, false, tabKey));

    return unsaveEventApi(eventId).then(() => {
        dispatch(trackSaveEvent({
            eventId,
            experimentId,
            savedByYou: false,
        }));

        // fetch more events if we don't have enough to display
        if (has(userSavedEvents, 'events') && size(userSavedEvents.events) < 8) {
            setTimeout(() => {
                dispatch(_fetchUserSavedEvents());
            }, 1000);
        }

        // Resolved promise so view knows that the event unsave was successful
        return Promise.resolve('success');
    }).catch(() => {
        dispatch(updateEventSavedByYou(eventId, true));
    });
};

const _trackBookmarkAttempt = ({eventId}) => ({
    type: 'BOOKMARK_ATTEMPT',
    meta: {
        analytics: {
            category: GA_HOME_CATEGORY,
            action: GA_BOOKMARK_ATTEMPT_ACTION,
            label: eventId,
            dimensions: {
                eventId,
            },
        },
    },
});

export const clickOnSaveEvent = (eventId, shouldSave) => (
    (dispatch, getState) => {
        const state = getState();
        const {
            user: {
                isAuthenticated,
            },
        } = state;

        if (isAuthenticated) {
            return _loggedInSaveAction(dispatch, state, eventId, shouldSave);
        }

        dispatch(_trackBookmarkAttempt({eventId}));

        return loggedOutSaveRedirect(eventId, {pathname: '/'});
    }
);

const finishFetchEventsForLocation = (dispatch, entities, entityContext, locationData) => {
    dispatch(setLoading(false));

    dispatch(updateContent({
        entities,
        entityContext,
    }));

    dispatch(updateLocation(locationData));

    dispatch(verifyPoorContent({buckets: entityContext, ActionName: 'search_location_change'}));
};

export const fetchEventsForLocation = (locationData) => (
    (dispatch) => {
        const {placeId} = locationData;

        dispatch(setLoading(true));

        return fetchEventsForLocationApi(placeId).then(({buckets}) => {
            let {entities, entityContext} = transformBucketsContent(buckets);

            const smallCityFallbackBucket = find(entityContext, {key: SMALL_CITY_FALLBACK});

            // when small_city_fallback, fetch more events and append them to the same bucket
            if (smallCityFallbackBucket && size(smallCityFallbackBucket.events) < 32) {
                return searchEvents(placeId).then((response) => {
                    const {
                        entities: newEntities,
                        ids,
                    } = transformSearchContent(response);

                    entities = {
                        ...entities,
                        ...newEntities,
                    };

                    entityContext = filter(entityContext, ({key}) => key !== SMALL_CITY_FALLBACK);
                    entityContext = [
                        ...entityContext,
                        {
                            ...smallCityFallbackBucket,
                            events: uniq([...ids, ...smallCityFallbackBucket.events]),
                        },
                    ];

                    finishFetchEventsForLocation(dispatch, entities, entityContext, locationData);
                });
            }

            return finishFetchEventsForLocation(dispatch, entities, entityContext, locationData);
        });
    }
);

export const UPDATE_TAB_CONTENT = 'UPDATE_TAB_CONTENT';
export const fetchEventsForTab = ({locationData, tabIndex = 0}) => (
    (dispatch, getState) => {
        dispatch(setLoading(true));

        const {
            location,
            user: {
                isAuthenticated,
            },
            content: {
                flatBucket,
            } = {},
        } = getState();

        const configKey = FEED_TABS[tabIndex];

        const {
            tabKey,
            eventSearch,
            follow,
            hasFiveToFollow,
        } = TABS_CONFIG[configKey];

        const followTags = follow;

        // locationData is empty when is called from tabs
        const _locationData = isEmpty(locationData) ? location : locationData;
        const {placeId, bbox, isOnline} = _locationData;

        // eventSearch is empty when is called from switch location
        const _eventSearch = isEmpty(eventSearch) ? get(flatBucket, 'eventSearch', {dates: 'current_future'}) : eventSearch;

        // followTags is empty when the bucket should not be rendered
        const _followTags = isEmpty(followTags) ? get(flatBucket, 'followTags', []) : followTags;

        dispatch(trackTabChange({loggedIn: isAuthenticated, tabKey: replace(tabKey, ' ', '')}));
        dispatch(updateTabKey(tabIndex));

        return searchEventsWithParameters({placeId, bbox, eventSearch: _eventSearch, isOnline}).then(({events}) => {
            dispatch({
                type: UPDATE_TAB_CONTENT,
                payload: {
                    events,
                    eventSearch: _eventSearch,
                    follow: _followTags,
                    hasFiveToFollow,
                },
            });
            dispatch(verifyPoorContent({events, actionName: `on_tab_change-${tabKey}`}));
            dispatch(setLoading(false));

            // if (!isEmpty(_followTags)) {
            //     dispatch(fetchOrganizersForLocation(_locationData, _followTags));
            // }

            // if (hasFiveToFollow) {
            //     fetchOrganizerArticles(locationData);
            // }
        });
    }
);

export const fetchEventsForYouTab = ({tabIndex}) => (
    (dispatch, getState) => {
        dispatch(setLoading(true));

        const {
            user: {
                isAuthenticated,
            },
            content: {
                flatBucket,
            } = {},
        } = getState();

        const configKey = FEED_TABS[tabIndex];

        const {
            tabKey,
            eventSearch,
            follow,
            hasFiveToFollow,
        } = TABS_CONFIG[configKey];

        const followTags = follow;

        // eventSearch is empty when is called from switch location or saved events
        const _eventSearch = isEmpty(eventSearch) ? get(flatBucket, 'eventSearch', {dates: 'current_future'}) : eventSearch;

        // followTags is empty when the bucket should not be rendered
        const _followTags = isEmpty(followTags) ? get(flatBucket, 'followTags', []) : followTags;

        dispatch(trackTabChange({loggedIn: isAuthenticated, tabKey: replace(tabKey, ' ', '')}));
        dispatch(updateTabKey(tabIndex));

        if (!isAuthenticated) {
            dispatch(setLoading(false));
            return dispatch({
                type: UPDATE_TAB_CONTENT,
                payload: {
                    events: [],
                    eventSearch: _eventSearch,
                    follow: _followTags,
                    hasFiveToFollow,
                },
            });
        }

        return getUserSavedEvents().then((results) => {
            const sortedEvents = sortBy(results.events, (event) => (event.start_date));
            const events = {
                pagination: results.pagination,
                results: sortedEvents,
            };

            dispatch({
                type: UPDATE_TAB_CONTENT,
                payload: {
                    events,
                    eventSearch: _eventSearch,
                    follow: _followTags,
                    hasFiveToFollow,
                },
            });
            dispatch(verifyPoorContent({events, actionName: `on_tab_change-${tabKey}`}));
            dispatch(setLoading(false));
        });
    }
);
